import React from 'react';
import { SectionTitle } from './SectionTitle';
import { ScrollAnimation } from './ScrollAnimation';
import { SpotlightCard } from './SpotlightCard';
import { CheckCircleIcon } from './icons/CheckCircleIcon';
import { AwardIcon } from './icons/AwardIcon';
import { SparkleIcon } from './icons/SparkleIcon';
import { WhatsappIcon } from './icons/WhatsappIcon';
import { WhatsAppButton } from './WhatsAppButton';
import { useTranslation } from '../i18n/LanguageContext';

interface PlanItem {
  title: string;
  duration: string;
  price: string;
  cashPrice?: string;
  description: string;
  features: string[];
  isPopular?: boolean;
  isBestValue?: boolean;
  badge?: string;
  whatsappMessage?: string;
  whatsappLink?: string;
}

const PlanCard: React.FC<PlanItem & { perMonthText: string; ctaText: string }> = ({
  title,
  duration,
  price,
  cashPrice,
  description,
  features,
  isPopular,
  isBestValue,
  badge,
  whatsappMessage,
  whatsappLink,
  perMonthText,
  ctaText,
}) => {
  const isHighlight = isPopular || isBestValue;

  const cardClasses = `
    relative bg-brand-primary/80 p-6 sm:p-8 rounded-2xl shadow-2xl flex flex-col h-full 
    transition-all duration-300 transform hover:-translate-y-2 border-2
    ${isHighlight ? 'border-brand-accent shadow-brand-accent/20' : 'border-slate-700/60 hover:border-brand-accent/50'}
  `;

  return (
    <SpotlightCard className={cardClasses}>
      {badge && (
        <div className="absolute top-0 -translate-y-1/2 left-1/2 -translate-x-1/2 w-max z-20">
          <div className="flex items-center gap-1.5 bg-gradient-to-r from-gradient-start to-gradient-end text-white px-4 py-1.5 rounded-full text-xs sm:text-sm font-semibold shadow-lg">
            {isBestValue ? <SparkleIcon className="w-4 h-4" /> : <AwardIcon className="w-4 h-4" />}
            {badge}
          </div>
        </div>
      )}

      <div className="text-center pt-2 mb-6">
        <div className="inline-block px-3 py-1 rounded-full bg-slate-800/80 text-brand-accent text-xs font-semibold tracking-wide uppercase mb-3 border border-slate-700/60">
          {duration}
        </div>
        <h3 className="text-2xl font-serif font-bold text-brand-light mb-2">{title}</h3>
        <p className="text-brand-text text-xs sm:text-sm min-h-[40px] flex items-center justify-center px-2">
          {description}
        </p>
      </div>

      <div className="bg-brand-secondary/80 border border-slate-700/50 rounded-xl p-4 text-center mb-6 relative overflow-hidden">
        <div className="flex items-baseline justify-center gap-1">
          <span className="text-3xl sm:text-4xl font-serif font-extrabold text-white tracking-tight">{price}</span>
          <span className="text-brand-accent font-medium text-sm">{perMonthText}</span>
        </div>
        {cashPrice && (
          <div className="mt-2 text-xs text-emerald-400 font-medium bg-emerald-950/40 border border-emerald-800/30 rounded-md py-1 px-2 inline-block">
            {cashPrice}
          </div>
        )}
      </div>

      <ul className="space-y-3 mb-8 flex-grow">
        {features.map((feature, index) => (
          <li key={index} className="flex items-start space-x-3">
            <CheckCircleIcon className="w-5 h-5 text-brand-accent flex-shrink-0 mt-0.5" aria-hidden="true" />
            <span className="text-brand-text text-xs sm:text-sm leading-relaxed">{feature}</span>
          </li>
        ))}
      </ul>

      <div className="mt-auto">
        <WhatsAppButton
          text={ctaText}
          customHref={whatsappLink}
          message={whatsappMessage}
          IconComponent={WhatsappIcon}
          className={`w-full text-sm sm:text-base py-3 px-4 font-semibold ${
            isHighlight
              ? 'bg-gradient-to-r from-gradient-start to-gradient-end text-white shadow-lg'
              : 'bg-slate-800 hover:bg-slate-700 text-brand-light border border-slate-600/60'
          }`}
        />
      </div>
    </SpotlightCard>
  );
};

export const PlansSection: React.FC = () => {
  const { t } = useTranslation();
  const plans: PlanItem[] = t('plans.items');

  return (
    <section id="planos" className="py-16 md:py-24">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <ScrollAnimation animation="slideInUp">
          <SectionTitle
            title={t('plans.title')}
            subtitle={<span dangerouslySetInnerHTML={{ __html: t('plans.subtitle') }} />}
          />
        </ScrollAnimation>
        <div className="grid lg:grid-cols-3 gap-8 max-w-6xl mx-auto mt-12 items-stretch">
          {plans.map((plan, index) => (
            <ScrollAnimation key={index} animation="slideInUp" delay={150 + index * 100}>
              <PlanCard
                {...plan}
                perMonthText={t('plans.perMonth')}
                ctaText={t('plans.ctaText')}
              />
            </ScrollAnimation>
          ))}
        </div>
      </div>
    </section>
  );
};
